'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submissionService } from '@/lib/services/submissionService';

interface FormData {
  name: string;
  phone: string;
  email: string;
  course: string;
}

export default function LeadFormSection() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    course: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const courses = [
    'AI Complete Course',
    'AI Powered Digital Marketing',
    'Graphic Designing',
    'Digital Marketing',
    'Coding',
    'Video Editing',
    'Website Development',
    'App Building',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await submissionService.createLeadSubmission(formData);
      
      if (result) {
        setSubmitMessage('Thank you! We will contact you soon for free counseling.');
        setFormData({ name: '', phone: '', email: '', course: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error('Lead form submission error:', error);
      setSubmitMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-20 md:py-32 px-6 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-secondary"></div>

      {/* Floating Icons Background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <Icon name="AcademicCapIcon" size={80} className="absolute top-20 left-10 text-white animate-pulse delay-100" />
        <Icon name="CodeBracketIcon" size={60} className="absolute top-40 right-20 text-white animate-pulse delay-300" />
        <Icon name="CpuChipIcon" size={70} className="absolute bottom-32 left-20 text-white animate-pulse delay-500" />
        <Icon name="RocketLaunchIcon" size={65} className="absolute bottom-20 right-32 text-white animate-pulse delay-700" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-4 py-2 mb-6">
            <Icon name="SparklesIcon" size={16} className="text-white" />
            <span className="text-xs font-semibold uppercase tracking-wider text-white">
              Start Your Journey
            </span>
          </span>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none text-white mb-4">
            Book Free<br />Counseling
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Get personalized guidance from our experts and kickstart your learning journey
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 md:p-12 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:bg-white/20 transition-all"
                placeholder="Enter your full name"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="[0-9]{10}"
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:bg-white/20 transition-all"
                placeholder="Enter 10-digit phone number"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:bg-white/20 transition-all"
                placeholder="Enter your email address"
              />
            </div>

            {/* Course */}
            <div>
              <label htmlFor="course" className="block text-sm font-semibold text-white mb-2">
                Course Interested *
              </label>
              <select
                id="course"
                name="course"
                value={formData.course}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white focus:outline-none focus:border-white focus:bg-white/20 transition-all"
              >
                <option value="" className="text-foreground">Select a course</option>
                {courses.map((course) => (
                  <option key={course} value={course} className="text-foreground">
                    {course}
                  </option>
                ))}
              </select>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-white text-primary px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-white/90 transition-all shadow-xl hover:shadow-2xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Book Free Counseling
                  <Icon name="ArrowRightIcon" size={20} />
                </>
              )}
            </button>

            {/* Success Message */}
            {submitMessage && (
              <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-xl p-4 text-center">
                <p className="text-white font-medium">{submitMessage}</p>
              </div>
            )}
          </form>
        </div>

        {/* Additional Info */}
        <div className="text-center mt-8 text-white/70 text-sm">
          <p>We respect your privacy. Your information is safe with us.</p>
        </div>
      </div>
    </section>
  );
}